// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@plumberssupply/App/Components/Notify";
import SystemBuilderProductCard from "@plumberssupply/App/Components/SystemBuilder/SystemBuilderProductCard";
import SystemBuilderRelatedProductsModal from "@plumberssupply/App/Components/SystemBuilder/SystemBuilderRelatedProductsModal";
import buildMiniSplitSystems from "@plumberssupply/App/Handlers/SystemBuilder/buildMiniSplitSystems";
import buildUnitarySystems from "@plumberssupply/App/Handlers/SystemBuilder/buildUnitarySystems";
import { CustomAppState, System, SystemBuilderProduct } from "@plumberssupply/App/Store/Reducers";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import { SystemBuilderKey } from "@plumberssupply/XDL/constants";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps {
    extendedStyles?: SystemBuilderYourSystemViewStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const { xSystemBuilder } = state;
    const {
        systemType,
        miniSplitOutputsystems,
        unitaryOutputsystems,
        filteredSystems,
        hasFilters,
        isPriceLoading,
        systemData,
    } = xSystemBuilder;
    const systems = hasFilters
        ? filteredSystems ?? []
        : systemType === "unitary"
        ? unitaryOutputsystems ?? []
        : miniSplitOutputsystems ?? [];
    return {
        systemType,
        systems,
        systemData,
        hasFilters,
        isPriceLoading,
        allowMultipleWishLists: getSettingsCollection(state).wishListSettings.allowMultipleWishLists,
        currencySymbol: state.context.session.currency?.currencySymbol ?? "",
        isTabletScreen: getXngCommon(state).isTabletScreen,
        showPricing: state.context.showPricing,
    };
};

const mapDispatchToProps = {
    addCartLines,
    setAddToListModalIsOpen,
    addToWishList,
    loadCurrentCart,
    buildUnitarySystems,
    buildMiniSplitSystems,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SystemBuilderYourSystemViewStyles {
    previous?: LinkPresentationProps;
    container?: InjectableCss;
    systemRank?: TypographyPresentationProps;
    specifications?: InjectableCss;
    components?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    note?: TypographyPresentationProps;
    systemWrapper?: InjectableCss;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    totalSection?: InjectableCss;
    addToList?: LinkPresentationProps;
    addToCartButton?: ButtonPresentationProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    addToListWrappper?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
    separator?: InjectableCss;
    overlayStyles?: InjectableCss;
    containerWrapper?: InjectableCss;
    specificationsWrapper?: InjectableCss;
    specificationsEntries?: InjectableCss;
}
export const systemBuilderYourSystemViewStyles: SystemBuilderYourSystemViewStyles = {};

const SystemBuilderYourSystemView = ({
    extendedStyles,
    systemType,
    systems,
    addCartLines,
    setAddToListModalIsOpen,
    addToWishList,
    allowMultipleWishLists,
    isPriceLoading,
    currencySymbol,
    loadCurrentCart,
    buildUnitarySystems,
    buildMiniSplitSystems,
    isTabletScreen,
    showPricing,
    systemData,
    hasFilters,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeWithCss(systemBuilderYourSystemViewStyles, extendedStyles));
    const [productId, setProductId] = useState("");
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const dispatch = useDispatch();

    const systemBuilderValue = JSON.parse(window.localStorage.getItem(SystemBuilderKey) || "{}");
    const { hasResults, data, type } = systemBuilderValue;

    useEffect(() => {
        // if we have previous results load them based on type
        if (hasResults && !systems.length) {
            if (type === "unitary") {
                buildUnitarySystems({
                    parameter: data,
                    onSuccess: result => {
                        dispatch({ type: "xSystemBuilder/SetSystems", systems: result.systems } as any);
                    },
                });
            } else {
                buildMiniSplitSystems({
                    parameter: data,
                    onSuccess: result => {
                        dispatch({ type: "xSystemBuilder/SetSystems", systems: result.systems } as any);
                    },
                });
            }
        }
    }, []);

    useEffect(() => {
        // return if no results or localStorage exists
        if (!systems.length || Object.keys(systemBuilderValue).length) {
            return;
        }

        window.localStorage.setItem(
            SystemBuilderKey,
            JSON.stringify({
                hasResults: true,
                step: 2,
                type: systemType,
                data: systemData,
            }),
        );
    }, []);

    const handleAddToCart = async (products: SystemBuilderProduct[], id: string, ahri: string) => {
        dispatch({
            type: "xSystemBuilder/SetCondenserProductId",
            productId: id,
        } as any);

        const productsInfos: any[] = [];
        products.forEach(product => {
            productsInfos.push({
                productId: product.id,
                unitOfMeasure: product.unitOfMeasure,
                qtyOrdered: product.qty,
            });
        });

        if (productsInfos && productsInfos.length > 0) {
            setIsAddingToCart(true);
            if (ahri) {
                // inject ahri number in one of the cartlines properties
                productsInfos[0].properties = { ahriNumber: ahri };
            }
            await addCartLines({
                productInfos: productsInfos,
                onSuccess: () => {
                    setIsAddingToCart(false);

                    toasterContext.addToast({
                        body: siteMessage("Custom_SystemBuilder_SystemHasBeenAddedToCart"),
                        messageType: "success",
                    });
                    loadCurrentCart({ shouldLoadFullCart: true });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult?.cartLines) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.apiResult);
                    }
                },
            });

            // display related products modal
            if (id) {
                dispatch({
                    type: "xSystemBuilder/UpdateIsModalOpen",
                    isModalOpen: true,
                } as any);
                setProductId(id);
            }
        }
    };

    const addAllToListClickHandler = (system: System) => {
        setProductId(system.condenserProductId);
        dispatch({
            type: "xSystemBuilder/SetCondenserProductId",
            productId: system.condenserProductId,
        } as any);

        const productInfos =
            system.products.map(product => ({
                productId: product.id,
                qtyOrdered: product.qty,
                unitOfMeasure: product.unitOfMeasure,
            })) || [];
        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: siteMessage("Custom_SystemBuilder_SystemHasBeenAddedToList"),
                        messageType: "success",
                    });
                    if (system.condenserProductId) {
                        dispatch({
                            type: "xSystemBuilder/UpdateIsModalOpen",
                            isModalOpen: true,
                        } as any);
                    }
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }
        setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    const SystemBuilderSystem: FC<{ system: System }> = ({ system }) => {
        return (
            <>
                {system.rank.toLowerCase() !== "other" && (
                    <Typography {...styles.systemRank} className={system.rank?.toLowerCase()}>
                        {system.rank}
                    </Typography>
                )}

                <StyledWrapper {...styles.specificationsWrapper}>
                    <Typography {...styles.sectionTitle}>{translate("Specifications")}</Typography>
                    <StyledWrapper {...styles.specificationsEntries} className="specifications">
                        <StyledWrapper {...styles.specifications}>
                            {Object.entries(system.mapping).map(([key, value]) => {
                                let specValue = system[key as keyof typeof system];
                                if (specValue?.toString()?.toLowerCase() === "false") {
                                    specValue = "No";
                                } else if (specValue?.toString()?.toLowerCase() === "true") {
                                    specValue = "Yes";
                                }
                                if (
                                    (key === "heatingAt5Dego70Degi" && specValue === 0) ||
                                    (key === "heatingAt32Dego70Degi" && specValue === 0) ||
                                    (key === "heatingAt47Dego70Degi" && specValue === 0)
                                ) {
                                    return null;
                                }
                                return (
                                    !!specValue && (
                                        <SmallHeadingAndText
                                            key={value}
                                            extendedStyles={styles.headingAndTextStyles}
                                            heading={value}
                                            text={specValue?.toString() || ""}
                                        />
                                    )
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                    <Typography {...styles.note}>{siteMessage("Custom_SystemBuilder_SpecificationsNote")}</Typography>
                </StyledWrapper>
                {system.products?.length > 0 && (
                    <>
                        <StyledWrapper {...styles.components}>
                            <Typography {...styles.sectionTitle}>{translate("Components")}</Typography>
                            {system.products.map(product => (
                                <SystemBuilderProductCard key={product.id} product={product} />
                            ))}
                        </StyledWrapper>
                        <StyledWrapper {...styles.totalSection}>
                            {showPricing && (
                                <>
                                    {isPriceLoading ? (
                                        <StyledWrapper>
                                            <LoadingSpinner {...styles.loadingSpinner} />
                                        </StyledWrapper>
                                    ) : (
                                        <SmallHeadingAndText
                                            heading={translate("Total")}
                                            text={`${currencySymbol}${Number(
                                                (system.sum || 0)?.toFixed(2),
                                            )?.toLocaleString()}`}
                                            extendedStyles={styles.totalHeadingAndText}
                                        />
                                    )}
                                </>
                            )}

                            <Button
                                sizeVariant="small"
                                {...styles.addToCartButton}
                                disabled={isPriceLoading || isAddingToCart}
                                onClick={() => handleAddToCart(system.products, system.condenserProductId, system.ahri)}
                            >
                                {translate("Add System to Cart")}
                            </Button>
                            <StyledWrapper {...styles.addToListWrappper}>
                                <Link onClick={() => addAllToListClickHandler(system)} {...styles.addToList}>
                                    {translate("Add All to List")}
                                </Link>
                            </StyledWrapper>
                        </StyledWrapper>
                    </>
                )}
            </>
        );
    };

    // Don't show message if hasResults is true - results exist but may still be loading
    if (!systems?.length && (!hasResults || hasFilters)) {
        return <Notify type="warning">{siteMessage("Custom_SystemBuilder_NoSystemAvailable")}</Notify>;
    }

    const getMaxNumOfSpecifications = (systems: any[]) => {
        if (!Array.isArray(systems) || systems.length === 0) {
            return 0;
        }

        const keysToCheck = ["heatingAt5Dego70Degi", "heatingAt32Dego70Degi", "heatingAt47Dego70Degi"];

        let maxCount = 0;

        systems.forEach(system => {
            let validKeyCount = 0;

            Object.entries(system.mapping).forEach(([key]) => {
                if (keysToCheck.includes(key) && system[key as keyof typeof system] === 0) {
                    return;
                }

                if (system[key as keyof typeof system]) {
                    validKeyCount++;
                }
            });

            maxCount = Math.max(maxCount, validKeyCount);
        });

        return Math.ceil(maxCount / 2);
    };

    const rankedSystems = systems?.filter(system => ["best", "better", "good"].includes(system.rank.toLowerCase()));
    const otherSystems = systems?.filter(system => system.rank.toLowerCase() === "other");

    const systemsMaxSpec = getMaxNumOfSpecifications(rankedSystems);
    const otherSystemsMaxSpec = getMaxNumOfSpecifications(otherSystems);
    return (
        <LoadingOverlay loading={!systems.length} {...styles.overlayStyles}>
            <StyledWrapper {...styles.containerWrapper}>
                <StyledWrapper {...styles.container} className={rankedSystems.length === 3 ? "threeRanked" : ""}>
                    {rankedSystems?.length > 0 &&
                        rankedSystems.map((system, index) => (
                            <StyledWrapper {...styles.systemWrapper} key={index} {...{ maxNumber: systemsMaxSpec }}>
                                <SystemBuilderSystem system={system} />
                            </StyledWrapper>
                        ))}
                </StyledWrapper>
            </StyledWrapper>
            {otherSystems?.length > 0 && (
                <>
                    <StyledWrapper {...styles.containerWrapper}>
                        <StyledWrapper {...styles.container} className="otherSystems">
                            {/* display other matching systems */}
                            {otherSystems.map((system, index) => (
                                <StyledWrapper
                                    {...styles.systemWrapper}
                                    key={index}
                                    {...{ maxNumber: otherSystemsMaxSpec }}
                                >
                                    <SystemBuilderSystem system={system} />
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                </>
            )}
            <SystemBuilderRelatedProductsModal productId={productId} />
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SystemBuilderYourSystemView);
